<template>
  <component
    :is="componentType"
    class="flex h-full w-full"
    :rel="link?.rel"
    :target="link?.target"
    :to="link?.href"
    @click="onGlobalLinkClick"
  >
    <Background
      :background
      class="w-full"
      :class="{
        [backgroundSizes]: fixedWidth && !!size,
        'p-16': withPadding,
        'shadow-short focus-within:shadow-long hover:shadow-long':
          link?.href && withPadding,
      }"
      container-class="flex flex-col gap-24 overflow-hidden"
      data-test="modular-card-container"
      rounded="always"
    >
      <ModularCardSection
        v-for="(section, sectionIndex) in parsedSections"
        :key="sectionIndex"
        v-bind="section"
        :blockPosition
        :blockTitle
        class="w-full"
        :data-test="`section-container-${sectionIndex}`"
      />
    </Background>
  </component>
</template>

<script lang="ts" setup>
import { computed, h } from 'vue'

import type { ModularCard } from '@backmarket/http-api/src/api-specs-content/models/modular-card'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { tw } from '@backmarket/utils/string/tw'
import type { Maybe } from '@backmarket/utils/types/Maybe'
import { RevButtonBase } from '@ds/components/ButtonBase'
import { cardWidths } from '@ds/components/CardCarousel/CardCarousel.constant'
import type { NodeRendererMap } from 'contentful-rich-text-vue-renderer'

import type { _Block } from '../../models/block'
import type { BlockProps } from '../../models/content-block'
import Background from '../../shared-components/Background/Background.vue'

import ModularCardSection from './ModularCardSection.vue'
// eslint-disable-next-line no-duplicate-imports
import type { ModularCardSection as ModularCardSectionType } from './ModularCardSection.vue'

export type ModularCardProps = (Omit<ModularCard, 'id' | 'sections'> & {
  cardPosition?: number
  blockTitle?: Maybe<string>
  fixedWidth?: boolean
  withPadding?: Maybe<boolean>
  sections: ModularCardSectionType[]
}) &
  BlockProps

const props = withDefaults(defineProps<ModularCardProps>(), {
  blockTitle: undefined,
  fixedWidth: true,
  withPadding: true,
  size: 'medium',
  cardPosition: 1,
})

const { trackClick } = useTracking()

const backgroundSizes = computed(() => {
  if (!props.size) return ''

  return cardWidths[props.size]
})

// There shouldn't be any headings in a RichText inside a Modular Card
// We cannot prevent that in Contentful. If ever editors pass a heading, we transform it into a heading but keep the style ("heading-x")
const transformHeadingsToParagraphs: NodeRendererMap = {
  'heading-1': (node, key, next) => {
    return h('p', { key, class: tw`heading-1` }, next(node.content, key, next))
  },
  'heading-2': (node, key, next) => {
    return h('p', { class: tw`heading-2` }, next(node.content, key, next))
  },
  'heading-3': (node, key, next) => {
    return h('p', { class: tw`heading-3` }, next(node.content, key, next))
  },
  'heading-4': (node, key, next) => {
    return h('p', { class: tw`heading-4` }, next(node.content, key, next))
  },
  'heading-5': (node, key, next) => {
    return h('p', { class: tw`heading-5` }, next(node.content, key, next))
  },
  'heading-6': (node, key, next) => {
    return h('p', { class: tw`heading-6` }, next(node.content, key, next))
  },
}

function slotMapper(
  slot: ModularCardSectionType['slots'][number],
): ModularCardSectionType['slots'][number] {
  if (slot.type === 'RichText') {
    const richTextProps = slot.props

    return {
      ...slot,

      props: {
        ...richTextProps,
        nodeRenderersOverrides: transformHeadingsToParagraphs,
        ignoreLinks: !!props.link,
      },
    } satisfies _Block<'RichText'>
  }

  if (slot.type === 'Media') {
    const mediaProps = slot.props

    if (!props.link) {
      return slot
    }

    return {
      ...slot,
      props: { ...mediaProps, link: undefined, ignoreLinks: true },
    } satisfies _Block<'Media'>
  }

  if (slot.type === 'ModularCardImage') {
    if (!props.link) {
      return slot
    }

    const modularCardImage = slot.props

    return {
      ...slot,
      props: { ...modularCardImage, link: undefined },
    } satisfies _Block<'ModularCardImage'>
  }

  if (slot.type === 'Button') {
    if (!props.link) {
      return slot
    }

    const slotProps = slot.props

    return {
      ...slot,
      props: {
        ...slotProps,
        ctaLink: {
          ...slotProps.ctaLink,
          link: {
            ...slotProps.ctaLink.link,
            href: '',
          },
        },
      },
    } satisfies _Block<'Button'>
  }

  return slot
}

// NB: parsedSections is not reactive. This simplifies the code and avoid infinite loops if we modify the props inside the computed
// If we need to make it reactive, we would need to use a computed and make sure to destructure the props inside the computed
const parsedSections = props.sections.map<ModularCardSectionType>((section) => {
  const parsedSlots = section.slots.map((slot) => slotMapper(slot))

  return { ...section, slots: parsedSlots, cardPosition: props.cardPosition }
})

const componentType = computed(() => (props.link ? RevButtonBase : 'div'))

function onGlobalLinkClick() {
  if (componentType.value === 'div') return

  trackClick({
    position: props.cardPosition,
    zone: props.tracking?.zone || '',
    value: 'ModularCard',
    blockPosition: props.blockPosition || 1,
    technicalName: props.technicalName || '',
    ...(props.blockTitle && { blockTitle: props.blockTitle }),
  })
}

// This event is common to every CMS block in order to hide the block containers
// on demand
defineEmits(['error', 'reset-error'])
</script>
